/**
 * @module common/components/filters_form
 */
import React, { PropTypes, Component } from 'react';
import classNames from 'classnames';
import without from 'lodash/without';
import { v4 } from 'node-uuid';
import { Field } from 'redux-form';
import { CheckBoxField } from 'common/components/form_fields';
import StarRating from 'common/components/star_rating';
import map from 'lodash/map';

/**
 * Filters form for different lists of elements
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class FiltersForm extends Component {
  constructor(props) {
    super(props);

    // An object that will hold the information about
    // dirty form fields
    this.state = {};

    this.sendForm = this.sendForm.bind(this);
    this.checkboxChanged = this.checkboxChanged.bind(this);
    this.toggleFilterOptions = this.toggleFilterOptions.bind(this);
  }

  getFilterId(filter) {
    return filter.key || filter.label || filter.field;
  }

  sendForm(data) {
    const newState = { ...this.state };
    for (const k of Object.keys(newState)) {
      newState[k].dirty = false;
    }

    this.setState({ ...newState });
    this.props.onSubmit(data);
  }

  /**
   * This function is called before marking a field as dirty to call props.onChange.
   *
   * @param {string} filterId - filter ID
   * @param {string} field - filter option name
   * @param {boolean} value - value to be set
   * @param {string} fieldName - field name in redux-form state
   * @returns {void}
   */
  checkboxChanged(filterId, field, value, fieldName) {
    this.props.onChange(fieldName, value);
    this.setFilterDirty(filterId, field);
  }

  /**
   * Marks filter as dirty to allow displaying submit button next to it.
   *
   * @param {string} filterId - filter ID
   * @param {string} field - filter option name
   * @returns {void}
   */
  setFilterDirty(filterId, field) {
    let newFilterState = null;
    const newState = { ...this.state };

    if (newState[filterId] && newState[filterId].changed) {
      // We want to remember all filter fields that were changed
      let modifiedFields = [...newState[filterId].changed];

      if (modifiedFields.indexOf(field) < 0) {
        modifiedFields.push(field);
      } else {
        modifiedFields = without(modifiedFields, field);
      }

      newFilterState = {
        ...newState[filterId],
        changed: modifiedFields,
        dirty: !!modifiedFields.length,
      };
    } else {
      newFilterState = {
        changed: [field],
        dirty: true,
      };
    }

    newState[filterId] = { ...this.state[filterId], ...newFilterState };
    this.setState({ ...newState });
    return newState;
  }

  /**
   * Toggles list of all available filter options
   *
   * @param {string} filterId - filter ID
   * @returns {void}
   */
  toggleFilterOptions(filterId) {
    let newFilterState = null;
    const newState = { ...this.state };

    if (newState[filterId]) {
      newFilterState = { ...this.state[filterId] };
      newFilterState.uncollapsed = !newFilterState.uncollapsed;
    } else {
      newFilterState = { uncollapsed: true };
    }

    newState[filterId] = { ...newFilterState };
    this.setState({ ...newState });
  }

  /**
   * Renders list of filter options or a single checkbox if not given
   * an array of options
   *
   * @param {string} field - field name
   * @param {array} opts - array of filter field options
   * @param {object} filter - filter object
   * @returns {node/array} fieldsArray/field - array of fields to render or a single node
   */
  renderOptions(field, opts, filter) {
    const { disabled } = this.props;
    const fieldsArray = [];
    const filterId = this.getFilterId(filter);
    const uncollapsed = this.state[filterId] && this.state[filterId].uncollapsed;

    if (opts && opts.length && Array.isArray(opts)) {
      const counter = uncollapsed ? opts.length : 5;

      for (let i = 0; i < counter; i += 1) {
        if (opts[i]) {
          const label = field === 'score' ? (<StarRating
            readonly={true}
            initialRate={opts[i][1]}
          />) : opts[i][1];

          // NOTE: the field name is a hack, sometimes we get a . from the backend
          // in the middle of the name, and this messes up redux-form naming
          // we replace all '.' with a '!' to mitigate this, and then replace it back
          // later when cleaning up the filters. Again, this is a huge and dirty hack
          // I've asked backend to clean this up, but it will take a large effort and we
          // don't have time right now.  We really should update this, and the specialization
          // filter names on the backend soon.
          // I'm going to go shower.  I feel dirty.
          fieldsArray.push(
            <Field
              name={`${field.replace('.', '!')}#${opts[i][0]}^${this.props.formName}`}
              label={label}
              component={CheckBoxField}
              disabled={disabled}
              customOnChange={(val) => {
                this.checkboxChanged(filterId, opts[i][1], val, `${field}#${opts[i][0]}`);
              }}
              key={v4()}
            />
          );
        }
      }

      if (opts.length > 5) {
        const toggleLink = (
          <a
            onClick={() => { this.toggleFilterOptions(filterId); }}
            key={v4()}
            className={classNames('collapse', 'show-more-toggle', { uncollapsed })}
          >
            Show {uncollapsed ? 'Less' : 'More'}
          </a>
        );
        fieldsArray.push(toggleLink);
      }

      return fieldsArray;
    }

    const name = typeof opts === 'boolean' || typeof opts === 'string'
      ? `${field}#${opts}^${this.props.formName}`
      : `${field}#true^${this.props.formName}`;

    return (<Field
      name={name}
      label={filter.label}
      component={CheckBoxField}
      disabled={disabled}
      customOnChange={(val) => {
        this.checkboxChanged(filterId, filter.label, val, name);
      }}
      key={v4()}
    />);
  }

  renderForm() {
    const { handleSubmit, filters, formName } = this.props;
    const { state } = this;

    // Special case:
    // form for projects gallery
    if (formName.match('gallery')) {
      let dirty = false;
      for (const v of Object.values(state)) {
        if (!dirty && v.dirty) {
          dirty = true;
        }
      }

      return (
        <form
          onSubmit={handleSubmit(this.sendForm)}
        >
          <div className="controls-group" key={v4()}>
            <h5>Filters</h5>
            { dirty &&
              <button
                type="submit"
                className="btn btn-sm btn-secondary apply-filters"
              >
                Apply
              </button>
            }
            { map(filters, (f) => {
              const filter = f[0];
              if (!filter) {
                return null;
              }
              return this.renderOptions(filter.field, filter.options, filter);
            })}
          </div>
        </form>
      );
    }

    return (
      <form
        onSubmit={handleSubmit(this.sendForm)}
      >
        { map(filters, (f, key) => {
          const firstFilter = f[0];
          if (!firstFilter) {
            return null;
          }

          if (firstFilter.type === 'boolean') {
            return null;
          }
          const isDirty = f.reduce((mem, filter) => {
            const filterId = this.getFilterId(filter);
            return (state[filterId] && state[filterId].dirty) || mem;
          }, false);

          return (
            <div className="controls-group" key={v4()}>
              {isDirty && <button
                type="submit"
                className="btn btn-secondary btn-sm apply-filters"
              >
                Apply
              </button>}
              <h5 className="filter-name">
                { f.length > 1 ? firstFilter.key : firstFilter.label }
              </h5>
              { f.map(filter => (this.renderOptions(filter.field, filter.options, filter)))}
            </div>
          );
        })}
      </form>
    );
  }

  render() {
    return (
      <div className="small-16 column controls-form">
        { this.renderForm() }
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {string} formName - name of the form, used to append to field names
 * @property {array} filters - list of filters to render
 * @property {bool} disabled - boolean value that sets the form fields to disabled
 * @property {func} onSubmit - function called when form is submitted
 * @property {func} onChange - function called when checkbox value changes
 * @property {func} handleSubmit - built-in redux-form function
 */
FiltersForm.propTypes = {
  formName: PropTypes.string,
  filters: PropTypes.array,
  disabled: PropTypes.bool,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  handleSubmit: PropTypes.func,
};



// WEBPACK FOOTER //
// ./common/components/filters_form.jsx